/**
 * Create / view / edit string registry entry (Config Registry–style layout, string-specific fields)
 */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Alert,
  TextField,
  MenuItem,
  Paper,
  IconButton,
  useTheme,
  Chip,
  FormControlLabel,
  Switch,
  Divider,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useGlobalConfig } from "context/GlobalContext";
import { useToast } from "context/ToastContext";
import { BglLoading } from "components/loading";
import { BglPrimarySmallButton } from "components/button/BglButtons";
import { ConfigRegistryApis } from "features/config-registry/api/configRegistryAPI";
import { LookupFieldSelect } from "features/config-registry/components/LookupFieldSelect";
import { UISchemaResponse, LookupType, LookupValue } from "features/config-registry/types/configRegistryTypes";
import { StringRegistryApis } from "../api/stringRegistryAPI";
import { StringRegistryFormData, StringRegistryItem } from "../types/stringRegistryTypes";
import { StringRegistryWorkflowButtons } from "../components/StringRegistryWorkflowButtons";

function formSignature(f: StringRegistryFormData): string {
  return JSON.stringify({
    stringResourceId: f.stringResourceId,
    displayName: f.displayName,
    documentation: f.documentation,
    deprecated: f.deprecated,
    selectedLookupIds: [...f.selectedLookupIds].sort(),
    localeRows: f.localeRows.map((r) => ({
      locale: r.locale,
      value: r.value
    }))
  });
}

const defaultForm = (): StringRegistryFormData => ({
  stringResourceId: "",
  displayName: "",
  documentation: "",
  deprecated: false,
  selectedLookupIds: [],
  localeRows: [{ locale: "en", value: "" }]
});

function itemToForm(item: StringRegistryItem): StringRegistryFormData {
  const selectedLookupIds = [
    ...(item.components || []).map((c) => c.documentId),
    ...(item.sections || []).map((s) => s.documentId)
  ];
  const firstTpl = item.templateItems?.[0];
  const localeRows =
    (firstTpl?.localeOverrides?.length ?? 0) > 0
      ? (firstTpl!.localeOverrides ?? []).map((lv) => ({
          locale: lv.locale,
          value: lv.value
        }))
      : [{ locale: "en", value: "" }];
  return {
    stringResourceId: item.stringResourceId,
    displayName: item.displayName,
    documentation: item.documentation || "",
    deprecated: !!item.deprecated,
    selectedLookupIds,
    localeRows
  };
}

export const StringRegistryFormPage = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  const mode = documentId ? (searchParams.get("mode") === "edit" ? "edit" : "view") : "create";

  const [form, setForm] = useState<StringRegistryFormData>(defaultForm());
  const [currentEntry, setCurrentEntry] = useState<StringRegistryItem | null>(null);
  const [baselineSig, setBaselineSig] = useState("");
  const [uiSchema, setUiSchema] = useState<UISchemaResponse | null>(null);
  const [publishedLocales, setPublishedLocales] = useState<{ code: string; name: string }[]>([]);
  const [loading, setLoading] = useState(!!documentId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canEdit = rc.permissionsManager.canStringRegistryEdit();
  const readOnly = mode === "view" || !canEdit;
  const fieldsLocked =
    readOnly || (mode === "edit" && currentEntry?.verificationStatus === "APPROVED");

  const isDirty = useMemo(
    () => !!(documentId && baselineSig && formSignature(form) !== baselineSig),
    [documentId, baselineSig, form]
  );

  const loadSchema = useCallback(async () => {
    const schemaRes = await ConfigRegistryApis.getUISchema(rc);
    if (schemaRes.data) {
      const schemaData = schemaRes.data as any;
      const raw = schemaData.lookupTypes || schemaData.lookupFields || [];
      const normalized = raw.map((lt: any) => ({
        ...lt,
        lookups: (lt.lookups || lt.options || []).map((opt: any) => ({
          ...opt,
          documentId: opt.documentId || opt.value,
          displayName: opt.displayName || opt.label || opt.code
        }))
      }));
      setUiSchema({ lookupTypes: normalized, hierarchy: schemaData.hierarchy || {} });
    }
    const locRes = await StringRegistryApis.getPublishedLocales(rc);
    if (locRes && !(locRes as any).error) {
      const raw = locRes as any;
      const d = raw.data;
      setPublishedLocales(Array.isArray(d) ? d : []);
    }
  }, [rc]);

  const loadEntry = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await StringRegistryApis.getStringWithTemplate(rc, documentId);
      if (res.error) {
        setError(res.error.message || "Failed to load");
        return;
      }
      const raw = res.data as any;
      const item: StringRegistryItem = raw?.data ?? raw;
      if (item?.stringResourceId) {
        const nextForm = itemToForm(item);
        setForm(nextForm);
        setCurrentEntry(item);
        setBaselineSig(formSignature(nextForm));
      }
    } catch (e: any) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [rc, documentId]);

  useEffect(() => {
    loadSchema();
  }, [loadSchema]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  // Taxonomy types are data-driven via the applicableToStringRegistry flag on the shared
  // lookup tables. Hardcoded COMPONENT/SECTION only when the backend predates the flag
  // (field absent on every type) — an explicit false always hides the dropdown.
  const taxonomyTypes: LookupType[] = useMemo(() => {
    const all = uiSchema?.lookupTypes || [];
    const flagsSupported = all.some((lt) => lt.applicableToStringRegistry !== undefined);
    return flagsSupported
      ? all.filter((lt) => lt.applicableToStringRegistry === true)
      : all.filter((lt) => lt.code === "COMPONENT" || lt.code === "SECTION");
  }, [uiSchema]);

  const optionsForType = (lt: LookupType) => (lt.lookups || lt.options || []) as LookupValue[];

  const idsForType = (lt: LookupType) => {
    const opts = optionsForType(lt);
    return form.selectedLookupIds.filter((id) => opts.some((o) => o.documentId === id));
  };

  const setLookupIdsForType = (lt: LookupType, ids: string | string[]) => {
    const arr = Array.isArray(ids) ? ids : ids ? [ids] : [];
    const typeOptionIds = new Set(optionsForType(lt).map((o) => o.documentId));
    setForm((f) => ({
      ...f,
      selectedLookupIds: [...f.selectedLookupIds.filter((id) => !typeOptionIds.has(id)), ...arr]
    }));
  };

  const handleSave = async () => {
    if (!form.stringResourceId.trim() || !form.displayName.trim()) {
      setError("stringResourceId and displayName are required");
      return;
    }
    const localeCodes = form.localeRows.map((r) => r.locale.trim()).filter(Boolean);
    if (localeCodes.length !== new Set(localeCodes).size) {
      setError("Each locale can only be used once. Remove or change duplicate locale rows.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (!documentId) {
        const createRes = await StringRegistryApis.createString(rc, {
          stringResourceId: form.stringResourceId.trim(),
          displayName: form.displayName.trim(),
          documentation: form.documentation || " ",
          deprecated: form.deprecated,
          lookupDocumentIds: form.selectedLookupIds
        });
        if (createRes.error) {
          setError(createRes.error.message || "Create failed");
          return;
        }
        const cr: any = createRes.data ?? createRes;
        const doc =
          cr?.documentId ||
          cr?.data?.documentId ||
          (cr?.data as any)?.id;
        if (!doc) {
          toast.error("Created but could not read document id — check Strapi response shape");
          navigate("/cms/string-registry");
          return;
        }
        const overrides = form.localeRows
          .filter((r) => r.locale.trim() && r.value.trim())
          .map((r) => ({
            locale: r.locale.trim(),
            value: r.value
          }));
        if (overrides.length > 0) {
          await StringRegistryApis.saveStringWithTemplate(rc, doc, [
            { localeOverrides: overrides }
          ]);
        }
        toast.success("String entry created");
        navigate(`/cms/string-registry/${doc}?mode=edit`);
        return;
      }

      const upd = await StringRegistryApis.updateString(rc, documentId, {
        displayName: form.displayName.trim(),
        documentation: form.documentation,
        deprecated: form.deprecated,
        lookupDocumentIds: form.selectedLookupIds
      });
      if (upd.error) {
        setError(upd.error.message || "Update failed");
        return;
      }

      const overrides = form.localeRows
        .filter((r) => r.locale.trim() && r.value.trim())
        .map((r) => ({
          locale: r.locale.trim(),
          value: r.value
        }));
      await StringRegistryApis.saveStringWithTemplate(rc, documentId, [
        { localeOverrides: overrides }
      ]);

      toast.success("Saved");
      await loadEntry();
    } catch (e: any) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addLocaleRow = () => {
    setForm((f) => {
      const used = new Set(f.localeRows.map((r) => r.locale.trim()).filter(Boolean));
      const nextCode =
        publishedLocales.map((l) => l.code).find((c) => !used.has(c)) || publishedLocales[0]?.code || "en";
      return {
        ...f,
        localeRows: [...f.localeRows, { locale: nextCode, value: "" }]
      };
    });
  };

  const localeOptionsForRow = (rowIndex: number) => {
    const current = form.localeRows[rowIndex]?.locale;
    const taken = new Set(
      form.localeRows
        .map((r, i) => (i === rowIndex ? null : r.locale.trim()))
        .filter(Boolean) as string[]
    );
    return publishedLocales.filter((l) => l.code === current || !taken.has(l.code));
  };

  const removeLocaleRow = (index: number) => {
    setForm((f) => ({
      ...f,
      localeRows: f.localeRows.filter((_, i) => i !== index)
    }));
  };

  if (loading && documentId) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <BglLoading />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 960, mx: "auto" }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate("/cms/string-registry")}
          sx={{ cursor: "pointer" }}
        >
          String Registry
        </Link>
        <Typography color="text.primary">
          {documentId ? (mode === "edit" ? "Edit" : "View") : "Create"}
        </Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <IconButton onClick={() => navigate("/cms/string-registry")} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600} sx={{ flexGrow: 1 }}>
            {documentId ? form.stringResourceId || "String entry" : "New string entry"}
          </Typography>
          {documentId && currentEntry && (
            <Chip
              size="small"
              label={currentEntry.verificationStatus || "TODO"}
              color={
                currentEntry.verificationStatus === "APPROVED"
                  ? "success"
                  : currentEntry.verificationStatus === "REJECTED"
                  ? "error"
                  : currentEntry.verificationStatus === "READY_FOR_QA"
                  ? "warning"
                  : "default"
              }
              sx={{ fontWeight: 600 }}
            />
          )}
          {/* Save icon — only shown when dirty (edit mode) */}
          {!fieldsLocked && isDirty && (
            <Tooltip title="Save changes">
              <span>
                <IconButton
                  onClick={handleSave}
                  disabled={saving}
                  color="primary"
                  size="small"
                  aria-label="Save"
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {documentId && currentEntry && mode !== "create" && (
            <StringRegistryWorkflowButtons
              key={`workflow-${currentEntry.documentId}-${currentEntry.verificationStatus}`}
              entry={currentEntry}
              isDirty={isDirty}
              onSaveFirst={handleSave}
              onActionComplete={async () => {
                await loadEntry();
                toast.success("Workflow updated");
              }}
              onError={(msg) => setError(msg)}
            />
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Section: Basic Information */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.75rem" }}>
              Basic Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="stringResourceId"
                required
                fullWidth
                value={form.stringResourceId}
                disabled={fieldsLocked || !!documentId}
                onChange={(e) => setForm((f) => ({ ...f, stringResourceId: e.target.value }))}
                helperText="Unique key (immutable after create). Use dot notation e.g. MY_FEATURE.TITLE"
              />
              <TextField
                label="Display Name"
                required
                fullWidth
                value={form.displayName}
                disabled={fieldsLocked}
                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              />
              <TextField
                label="Documentation"
                required
                fullWidth
                multiline
                minRows={3}
                value={form.documentation}
                disabled={fieldsLocked}
                onChange={(e) => setForm((f) => ({ ...f, documentation: e.target.value }))}
              />
            </Box>
          </Box>

          <Divider />

          {/* Section: Taxonomy */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.75rem" }}>
              Classification
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {taxonomyTypes.map((lt) => (
                <LookupFieldSelect
                  key={lt.code}
                  lookupType={{ ...lt, allowMultiSelect: true } as LookupType}
                  options={optionsForType(lt)}
                  value={idsForType(lt)}
                  onChange={(v) => setLookupIdsForType(lt, v)}
                  disabled={fieldsLocked}
                  tooltip={`${lt.code} lookups (classification shared across registries)`}
                />
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Section: GLOBAL template locale values */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.75rem" }}>
              GLOBAL Template Values
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {form.localeRows.map((row, index) => (
                <Box key={`${row.locale}-${index}`} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <TextField
                    select
                    label="Locale"
                    size="small"
                    sx={{ width: 140 }}
                    value={row.locale}
                    disabled={fieldsLocked}
                    onChange={(e) => {
                      const next = [...form.localeRows];
                      next[index] = { ...next[index], locale: e.target.value };
                      setForm((f) => ({ ...f, localeRows: next }));
                    }}
                  >
                    {localeOptionsForRow(index).map((l) => (
                      <MenuItem key={l.code} value={l.code}>
                        {l.name} ({l.code})
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Text"
                    fullWidth
                    size="small"
                    multiline
                    value={row.value}
                    disabled={fieldsLocked}
                    onChange={(e) => {
                      const next = [...form.localeRows];
                      next[index] = { ...next[index], value: e.target.value };
                      setForm((f) => ({ ...f, localeRows: next }));
                    }}
                  />
                  {!fieldsLocked && (
                    <IconButton size="small" onClick={() => removeLocaleRow(index)} aria-label="Remove locale">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
              {!fieldsLocked && (
                <Box>
                  <BglPrimarySmallButton startIcon={<AddIcon />} onClick={addLocaleRow}>
                    Add locale row
                  </BglPrimarySmallButton>
                </Box>
              )}
            </Box>
          </Box>

          <Divider />

          {/* Section: Flags */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.75rem" }}>
              Flags
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.deprecated}
                  onChange={(e) => setForm((f) => ({ ...f, deprecated: e.target.checked }))}
                  disabled={readOnly}
                  color="warning"
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Deprecated
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Mark this string as no longer in active use
                  </Typography>
                </Typography>
              }
            />
          </Box>

          {/* Create mode: explicit Save button */}
          {!documentId && (
            <Box sx={{ mt: 1 }}>
              <BglPrimarySmallButton startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Create string entry"}
              </BglPrimarySmallButton>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default StringRegistryFormPage;
